import { Container } from 'pixi.js';
import { ART, MAP_H, MAP_W } from '../shared/constants';
import { clamp } from '../shared/util';

/**
 * How briskly the camera closes the gap to where it is going, per second.
 * High enough that a jump across the map takes about a second, low enough that
 * the eye can follow the ground the whole way.
 */
const GLIDE_RATE = 4.2;

export class Camera {
  /**
   * The world's extent in art pixels. Not a constant: each match is played on a
   * rectangle sized to its roster, and panning out over the dead ocean beyond it
   * would make a small world feel broken rather than small.
   */
  worldW = MAP_W * ART;
  worldH = MAP_H * ART;

  x = this.worldW / 2;
  y = this.worldH / 2;
  /*
   * Halved along with the doubling of `ART`. A tile is twice as wide in world
   * pixels now, so the old numbers would frame a quarter of the ground and the
   * player would open the game already zoomed into a field.
   */
  zoom = 1.2;

  /**
   * Where the camera is heading, and how fast it closes the gap.
   *
   * Snapping the view somewhere is disorienting: the map cuts, and you have to
   * work out where you now are. Gliding keeps the ground continuous under you,
   * so choosing a homeland *travels* there and you can read the coastline on the
   * way. The rate is per second and applied exponentially, so the motion is
   * frame-rate independent — the same swoop at 30 fps and at 144.
   */
  targetX = this.x;
  targetY = this.y;
  targetZoom = 2.4;

  /**
   * True once the player has taken the camera into their own hands. Anything
   * that follows the action (the spawn-phase homeland glide) gives way the
   * instant this is set, because a camera that fights the mouse is worse than
   * no camera help at all.
   */
  userControlled = false;

  screenW = 1;
  screenH = 1;

  setScreen(w: number, h: number) {
    this.screenW = w;
    this.screenH = h;
  }

  /**
   * Pull back until the whole world fits on screen — the view you choose a
   * homeland from. You cannot pick a coastline you cannot see.
   */
  fitWorld() {
    const fit = Math.min(this.screenW / this.worldW, this.screenH / this.worldH);
    this.zoom = this.targetZoom = clamp(fit * 0.98, 0.225, 5);
    this.x = this.targetX = this.worldW / 2;
    this.y = this.targetY = this.worldH / 2;
    this.userControlled = false;
    this.clampPos();
  }

  /** Ask the camera to travel somewhere, over the next second or so. */
  glideTo(tx: number, ty: number, zoom?: number) {
    this.targetX = tx * ART;
    this.targetY = ty * ART;
    if (zoom !== undefined) this.targetZoom = clamp(zoom, 0.225, 5);
  }

  /** The player has asked for this view; nothing may glide it away again. */
  takeOver() {
    this.userControlled = true;
    this.targetX = this.x;
    this.targetY = this.y;
    this.targetZoom = this.zoom;
  }

  /** One frame of travel. `dt` in seconds. */
  step(dt: number) {
    if (dt <= 0) return;
    const k = 1 - Math.exp(-GLIDE_RATE * Math.min(0.25, dt));
    this.zoom += (this.targetZoom - this.zoom) * k;
    this.x += (this.targetX - this.x) * k;
    this.y += (this.targetY - this.y) * k;
    // close enough is arrived, or the camera creeps for ever at a hundredth of
    // a pixel and never lets the follow logic settle
    if (Math.abs(this.targetX - this.x) < 0.05) this.x = this.targetX;
    if (Math.abs(this.targetY - this.y) < 0.05) this.y = this.targetY;
    if (Math.abs(this.targetZoom - this.zoom) < 0.001) this.zoom = this.targetZoom;

    const px = this.x, py = this.y;
    this.clampPos();
    /*
     * A destination outside the world can never be reached — give it up rather
     * than press against the edge for ever and never count as arrived.
     *
     * **Only once the zoom has stopped moving, though.** `clampPos` keeps the
     * view inside the world *at the current zoom*, and a camera that is still
     * pulled back sees more world than there is: `worldW < halfW * 2` is true,
     * so it is pinned dead centre. Writing that back into the destination is
     * what broke the flight home — the trip starts from the whole-map view, so
     * on its very first frame the target became "the middle of the map" and the
     * camera dutifully zoomed in on the ocean while your kingdom sat off screen.
     * While the zoom is closing in, the clamp is a temporary fact about a view
     * that is still changing, and the destination outlives it.
     */
    if (this.zoom === this.targetZoom) {
      if (this.x !== px) this.targetX = this.x;
      if (this.y !== py) this.targetY = this.y;
    }
  }

  /** Called when a match starts, with the world size the server rolled. */
  setWorld(tilesW: number, tilesH: number) {
    this.worldW = tilesW * ART;
    this.worldH = tilesH * ART;
    this.x = Math.min(this.x, this.worldW);
    this.y = Math.min(this.y, this.worldH);
    this.clampPos();
    this.targetX = this.x;
    this.targetY = this.y;
    this.targetZoom = this.zoom;
    this.userControlled = false;
  }

  zoomAt(delta: number, sx: number, sy: number) {
    this.zoomBy(delta > 0 ? 1.2 : 1 / 1.2, sx, sy);
  }

  /**
   * Multiply the zoom by an arbitrary factor, keeping one screen point still.
   *
   * A wheel notch is a *step* and can be a constant; a pinch is continuous and
   * cannot — the factor is the ratio the two fingers have moved apart since the
   * last frame. Same anchor arithmetic either way: convert the point to world
   * space before and after, and shift the camera by the difference, so the map
   * grows out of the spot being touched rather than out of the middle.
   */
  zoomBy(factor: number, sx: number, sy: number) {
    const before = this.screenToWorld(sx, sy);
    this.zoom = clamp(this.zoom * factor, 0.225, 5);
    const after = this.screenToWorld(sx, sy);
    this.x += before.x - after.x;
    this.y += before.y - after.y;
    this.clampPos();
    this.takeOver();
  }

  panScreen(dx: number, dy: number) {
    this.x -= dx / this.zoom;
    this.y -= dy / this.zoom;
    this.clampPos();
    this.takeOver();
  }

  clampPos() {
    const halfW = this.screenW / (2 * this.zoom);
    const halfH = this.screenH / (2 * this.zoom);
    if (this.worldW < halfW * 2) this.x = this.worldW / 2;
    else this.x = clamp(this.x, halfW, this.worldW - halfW);
    if (this.worldH < halfH * 2) this.y = this.worldH / 2;
    else this.y = clamp(this.y, halfH, this.worldH - halfH);
  }

  apply(world: Container) {
    world.scale.set(this.zoom);
    world.position.set(
      Math.round(this.screenW / 2 - this.x * this.zoom),
      Math.round(this.screenH / 2 - this.y * this.zoom),
    );
  }

  /** screen px -> world art px */
  screenToWorld(sx: number, sy: number) {
    return {
      x: (sx - this.screenW / 2) / this.zoom + this.x,
      y: (sy - this.screenH / 2) / this.zoom + this.y,
    };
  }

  /** screen px -> tile coordinates (floats) */
  screenToTile(sx: number, sy: number) {
    const w = this.screenToWorld(sx, sy);
    return { x: w.x / ART, y: w.y / ART };
  }

  /** True while the camera is still travelling somewhere. */
  get gliding() {
    return Math.abs(this.targetX - this.x) > 0.05
      || Math.abs(this.targetY - this.y) > 0.05
      || Math.abs(this.targetZoom - this.zoom) > 0.001;
  }

  worldToScreen(wx: number, wy: number) {
    return {
      x: (wx - this.x) * this.zoom + this.screenW / 2,
      y: (wy - this.y) * this.zoom + this.screenH / 2,
    };
  }

  /** visible rectangle in tile coordinates */
  viewRect() {
    const a = this.screenToTile(0, 0);
    const b = this.screenToTile(this.screenW, this.screenH);
    return { x0: a.x, y0: a.y, x1: b.x, y1: b.y };
  }
}
